import { useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Clock, AlertCircle, LogIn, LogOut, Coffee, Timer, Pencil, Trash2, Plus, Radio,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { PERMISSIONS } from '@pms/shared';
import {
  attendanceService,
  type PunchLogWithUser,
  type PunchSegment,
} from '@/services/attendance.service';
import { formatAttendanceHours } from '@/lib/duration';
import { usePermission } from '@/hooks/usePermission';
import { EditPunchModal } from './EditPunchModal';
import { DeletePunchModal } from './DeletePunchModal';
import { AddPunchModal } from './AddPunchModal';

// ── Helpers ────────────────────────────────────────────────────────────────────

function toIST(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
  });
}

function splitTime(iso: string): { hm: string; ampm: string } {
  const s = toIST(iso);
  const parts = s.split(' ');
  return { hm: parts[0] ?? s, ampm: parts[1] ?? '' };
}

// No duration arithmetic lives on the client. Working/break minutes and the per-segment
// durations are computed once on the server (buildAttendanceSegments) and read from the
// same daily_attendance_records row the attendance report list shows — previously this
// modal recomputed the day with Math.round per pair while the server used Math.floor,
// so the two screens disagreed (9h 7m here vs 9h 3m on the report).

function getInitials(name: string): string {
  return name.trim().split(/\s+/).map((w) => w[0] ?? '').join('').toUpperCase().slice(0, 2) || '?';
}

/** Ticks subscribers once a second. Module-level so the reference stays stable. */
function subscribeToSecond(onChange: () => void): () => void {
  const id = window.setInterval(onChange, 1000);
  return () => window.clearInterval(id);
}

/** No clock at all, for the days that have nothing running. */
function subscribeToNothing(): () => void {
  return () => {};
}

/** Whole seconds, so the snapshot only changes once a second rather than every render. */
function getSecondsNow(): number {
  return Math.floor(Date.now() / 1000);
}

/**
 * Seconds elapsed since an open check-in, ticking once a second.
 *
 * The one duration on this screen that is not server-computed, because it has no end yet —
 * it is the stretch the employee is still working. Every closed pair still comes from
 * buildAttendanceSegments, so the live figure and the stored totals cannot drift apart on
 * anything already finished. `since` is null when the day has no open check-in, which
 * unsubscribes from the clock.
 */
function useElapsedSeconds(since: string | null): number | null {
  const nowSec = useSyncExternalStore(
    since ? subscribeToSecond : subscribeToNothing,
    getSecondsNow,
  );
  if (!since) return null;
  return Math.max(0, nowSec - Math.floor(new Date(since).getTime() / 1000));
}

/** "2h 04m 37s" — seconds shown so the running stretch visibly ticks. */
function formatElapsed(totalSeconds: number): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return h > 0 ? `${h}h ${pad(m)}m ${pad(s)}s` : `${m}m ${pad(s)}s`;
}

// ── Horizontal Timeline ────────────────────────────────────────────────────────

function HorizontalPunchTimeline({
  logs,
  segments,
  userName,
  onEdit,
  onDelete,
  canDelete,
  runningSeconds,
  readOnly = false,
}: {
  logs: PunchLogWithUser[];
  segments: PunchSegment[];
  userName: string;
  onEdit: (l: PunchLogWithUser) => void;
  onDelete: (l: PunchLogWithUser) => void;
  canDelete: boolean;
  /** Ticking length of the still-open final stretch, or null when the day is closed. */
  runningSeconds: number | null;
  /** Drops the per-card Edit / Delete icon bar. */
  readOnly?: boolean;
}) {
  type TItem =
    | { kind: 'punch'; log: PunchLogWithUser; seq: number }
    | { kind: 'gap'; mins: number; isBreak: boolean }
    | { kind: 'running'; seconds: number };

  // Interleave each punch with the server-computed segment that starts at it. Segment
  // minutes are derived from the day's running total, so the chips add up exactly to the
  // WORKING / BREAK tiles below.
  const segmentByFromId = new Map(segments.map((s) => [s.from_punch_id, s]));

  const items: TItem[] = [];
  logs.forEach((log, idx) => {
    items.push({ kind: 'punch', log, seq: idx + 1 });
    const segment = segmentByFromId.get(log.id);
    if (segment && logs[idx + 1]) {
      items.push({ kind: 'gap', mins: segment.minutes, isBreak: segment.kind === 'break' });
    }
  });

  // The open stretch has no closing punch, so it trails the last node rather than sitting
  // between two of them.
  if (runningSeconds !== null) {
    items.push({ kind: 'running', seconds: runningSeconds });
  }

  return (
    <div
      className="overflow-x-auto py-2"
      style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
    >
      <div className="relative inline-flex items-start px-4 py-1 sm:px-6">

        {/* ── Spine ── */}
        <div className="pointer-events-none absolute left-4 right-4 top-[80px] z-0 flex items-center sm:left-6 sm:right-6 sm:top-[96px]">
          <div className="h-0.5 flex-1 rounded-full bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-600 dark:via-slate-500 dark:to-slate-600" />
          <svg width="10" height="10" viewBox="0 0 10 10" className="shrink-0 text-slate-300 dark:text-slate-600" fill="none">
            <path d="M0 5h8M5 2l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {items.map((item, i) => {

          /* ── Running pill — the stretch still in progress ── */
          if (item.kind === 'running') {
            return (
              <div
                key="running"
                className="relative flex min-w-[104px] flex-col items-center sm:min-w-[128px]"
              >
                <div className="h-12 sm:h-14" />
                <div className="relative z-20 flex h-16 w-full items-center justify-center sm:h-20">
                  <div className="flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-700 shadow-sm whitespace-nowrap sm:gap-1.5 sm:px-3 sm:py-1.5 sm:text-[11px] dark:border-emerald-700/60 dark:bg-emerald-900/30 dark:text-emerald-300">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                    </span>
                    <span className="tabular-nums">{formatElapsed(item.seconds)}</span>
                  </div>
                </div>
                <div className="h-12 sm:h-14" />
                <div className="h-6" />
              </div>
            );
          }

          /* ── Gap pill ── */
          if (item.kind === 'gap') {
            return (
              <div
                key={`gap-${i}`}
                className="relative flex min-w-[104px] flex-col items-center sm:min-w-[128px]"
              >
                <div className="h-12 sm:h-14" />
                <div className="relative z-20 flex h-16 w-full items-center justify-center sm:h-20">
                  <div className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-semibold shadow-sm whitespace-nowrap sm:gap-1.5 sm:px-3 sm:py-1.5 sm:text-[11px] ${
                    item.isBreak
                      ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800/50 dark:bg-amber-900/20 dark:text-amber-300'
                      : 'border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-800/50 dark:bg-teal-900/20 dark:text-teal-300'
                  }`}>
                    {item.isBreak
                      ? <Coffee className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                      : <Timer  className="h-3 w-3 sm:h-3.5 sm:w-3.5" />}
                    <span>{item.isBreak ? 'Break' : 'Work'}: {formatAttendanceHours(item.mins)}</span>
                  </div>
                </div>
                <div className="h-12 sm:h-14" />
                <div className="h-6" />
              </div>
            );
          }

          /* ── Punch node ── */
          const { log } = item;
          const isIn  = log.punch_type === 'check_in';
          const { hm, ampm } = splitTime(log.punch_time);

          return (
            <div key={log.id} className="flex w-[104px] flex-col items-center sm:w-[136px]">

              {/* Top: check-in time or empty stub */}
              <div className="flex h-12 w-full flex-col items-center justify-end pb-2 sm:h-14">
                {isIn ? (
                  <>
                    <div className="text-center">
                      <p className="text-[12px] font-bold tabular-nums leading-none text-emerald-700 dark:text-emerald-400 sm:text-[13px]">{hm}</p>
                      <p className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide text-emerald-500 dark:text-emerald-500">{ampm}</p>
                    </div>
                    <div className="mt-1.5 h-3 w-px rounded-full bg-emerald-300 dark:bg-emerald-600" />
                  </>
                ) : (
                  <div className="mt-auto h-3 w-px rounded-full bg-rose-200 dark:bg-rose-700" />
                )}
              </div>

              {/* ── Punch card ── */}
              <div className="relative z-10 flex shrink-0 flex-col items-center gap-1.5">
                {/* Photo / avatar */}
                <div className={`relative h-24 w-24 overflow-hidden rounded-xl border-2 shadow-md sm:h-32 sm:w-32 sm:shadow-lg ${
                  isIn ? 'border-emerald-400 dark:border-emerald-600' : 'border-rose-400 dark:border-rose-600'
                }`}>
                  <div className={`flex h-full w-full items-center justify-center text-base font-bold text-white sm:text-lg ${
                    isIn
                      ? 'bg-gradient-to-br from-emerald-400 to-emerald-600'
                      : 'bg-gradient-to-br from-rose-400 to-rose-600'
                  }`}>
                    {getInitials(userName)}
                  </div>
                  {log.raw_image_url && (
                    <img
                      src={log.raw_image_url}
                      alt="punch photo"
                      className="absolute inset-0 h-full w-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                </div>
                {/* Icon bar — outside the photo */}
                {!readOnly && (
                  <div className={`flex items-center justify-center gap-1.5 rounded-full border px-1.5 py-1 shadow-sm ${
                    isIn
                      ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-800/50 dark:bg-emerald-900/30'
                      : 'border-rose-200 bg-rose-50 dark:border-rose-800/50 dark:bg-rose-900/30'
                  }`}>
                    <button
                      onClick={() => onEdit(log)}
                      title="Edit punch"
                      className={`flex h-5 w-5 items-center justify-center rounded-full transition-colors ${
                        isIn
                          ? 'text-emerald-600 hover:bg-emerald-100 dark:text-emerald-400 dark:hover:bg-emerald-900/50'
                          : 'text-rose-600 hover:bg-rose-100 dark:text-rose-400 dark:hover:bg-rose-900/50'
                      }`}
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                    {canDelete && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onDelete(log); }}
                        title="Delete punch"
                        className="flex h-5 w-5 items-center justify-center rounded-full text-red-500 transition-colors hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/40"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Bottom: check-out time or empty stub */}
              <div className="flex h-12 w-full flex-col items-center justify-start pt-2 sm:h-14">
                {!isIn ? (
                  <>
                    <div className="mb-1.5 h-3 w-px rounded-full bg-rose-300 dark:bg-rose-600" />
                    <div className="text-center">
                      <p className="text-[12px] font-bold tabular-nums leading-none text-rose-700 dark:text-rose-400 sm:text-[13px]">{hm}</p>
                      <p className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide text-rose-500 dark:text-rose-500">{ampm}</p>
                    </div>
                  </>
                ) : (
                  <div className="h-3 w-px rounded-full bg-emerald-200 dark:bg-emerald-700" />
                )}
              </div>

              {/* IN / OUT pill */}
              <span className={`mt-1 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider sm:px-2.5 ${
                isIn
                  ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                  : 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400'
              }`}>
                {isIn ? 'IN' : 'OUT'}
              </span>

            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Props ──────────────────────────────────────────────────────────────────────

interface Props {
  userId:   string;
  userName: string;
  photoUrl?: string;
  date:     string;
  onClose:  () => void;
  /** View the day's punches only: no Add / Edit / Delete anywhere in the modal. */
  readOnly?: boolean;
}

// ── Modal ──────────────────────────────────────────────────────────────────────

export function PunchTimelineModal({ userId, userName, photoUrl, date, onClose, readOnly = false }: Props) {
  const [editTarget,   setEditTarget]   = useState<PunchLogWithUser | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PunchLogWithUser | null>(null);
  const [showAddPunch, setShowAddPunch] = useState(false);

  const { has } = usePermission();

  const { data: myPerm } = useQuery({
    queryKey: ['my-punch-delete-permission'],
    queryFn:  () => attendanceService.getMyDeletePermission(),
    enabled:   !readOnly && has(PERMISSIONS.ATTENDANCE_MANAGE),
  });

  const canDelete = !readOnly && has(PERMISSIONS.ATTENDANCE_MANAGE) && (
    !myPerm ||
    myPerm.mode === 'disabled' ||
    myPerm.can_delete_all === true ||
    (myPerm.mode === 'person_wise' && myPerm.allowed_target_ids.includes(userId))
  );

  const displayDate = new Date(date + 'T00:00:00').toLocaleDateString('en-GB', {
    weekday: 'long', day: '2-digit', month: 'short', year: 'numeric',
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['attendance', 'timeline', userId, date],
    queryFn:  () => attendanceService.getPunchTimeline(userId, date),
    // Poll only while a stretch is open: the totals move the moment the employee punches
    // out, and this modal is exactly where someone watches for that. A closed day cannot
    // change on its own, so it needs no polling.
    refetchInterval: (query) => (query.state.data?.totals.open_check_in ? 30_000 : false),
  });

  // Already ordered ASC by the server, over the whole IST day (no page limit).
  const logs     = data?.punches  ?? [];
  const segments = data?.segments ?? [];
  const totals   = data?.totals;

  // Ticks only while the day ends on an unclosed check-in; null otherwise stops the timer.
  const runningSeconds = useElapsedSeconds(totals?.running_since ?? null);

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-modal flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="relative flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-border dark:bg-card">

          {/* ── Header ── */}
          <div className="flex shrink-0 items-center gap-3 border-b border-slate-100 px-4 py-3 dark:border-border/60 sm:px-5 sm:py-4">
            <div className="h-9 w-9 shrink-0 overflow-hidden rounded-full border-2 border-teal-100 shadow-sm dark:border-teal-900/50 sm:h-10 sm:w-10">
              {photoUrl ? (
                <img src={photoUrl} alt={userName} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-teal-500 to-teal-700 text-xs font-bold text-white">
                  {getInitials(userName)}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100">Punch Timeline</h2>
              <p className="truncate text-xs text-slate-400 dark:text-slate-500">{userName} · {displayDate}</p>
            </div>
            {!readOnly && (
              <button
                onClick={() => setShowAddPunch(true)}
                title="Add Punch"
                className="flex shrink-0 items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 dark:border-emerald-800/50 dark:bg-emerald-900/20 dark:text-emerald-400 dark:hover:bg-emerald-900/40"
              >
                <Plus className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Add Punch</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded-xl p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-muted dark:hover:text-slate-300"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* ── Body ── */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-teal-500 dark:border-slate-700 dark:border-t-teal-400" />
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center py-14 text-center px-4">
              <AlertCircle className="mb-2 h-8 w-8 text-rose-400" />
              <p className="text-sm font-semibold text-rose-600 dark:text-rose-400">Failed to load punch logs</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-4">
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 dark:bg-muted">
                <Clock className="h-6 w-6 text-slate-300 dark:text-slate-600" />
              </div>
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">No punches recorded</p>
              <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">No punch logs found for this day</p>
              {!readOnly && (
                <button
                  onClick={() => setShowAddPunch(true)}
                  className="mt-4 flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700"
                >
                  <Plus className="h-4 w-4" />
                  Add Punch
                </button>
              )}
            </div>
          ) : (
            <>
              {/* ── Legend ── */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-slate-100 px-4 py-2.5 dark:border-border/60 sm:px-5">
                <span className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500">
                    <LogIn className="h-2.5 w-2.5 text-white" />
                  </span>
                  Check In — time above
                </span>
                <span className="hidden text-slate-200 dark:text-slate-700 sm:inline">·</span>
                <span className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-rose-500">
                    <LogOut className="h-2.5 w-2.5 text-white" />
                  </span>
                  Check Out — time below
                </span>
                {!readOnly && (
                  <span className="ml-auto flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500">
                    <Pencil className="h-3 w-3" />
                    Hover to edit / delete
                  </span>
                )}
              </div>

              {/* ── Live banner — only while a stretch is still open ── */}
              {runningSeconds !== null && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-emerald-100 bg-emerald-50/70 px-4 py-2.5 dark:border-emerald-900/40 dark:bg-emerald-900/20 sm:px-5">
                  <Radio className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                    Clocked in — {formatElapsed(runningSeconds)}
                  </span>
                  <span className="text-[11px] text-emerald-600/80 dark:text-emerald-400/80">
                    counts towards the totals below once they punch out
                  </span>
                </div>
              )}

              {/* ── Horizontal timeline ── */}
              <div className="px-1 py-1">
                <HorizontalPunchTimeline logs={logs} segments={segments} userName={userName} onEdit={setEditTarget} onDelete={setDeleteTarget} canDelete={canDelete} runningSeconds={runningSeconds} readOnly={readOnly} />
              </div>

              {/* ── Summary tiles ── */}
              <div className="border-t border-slate-100 px-4 py-3 dark:border-border/60 sm:px-5">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                  <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2.5 text-center dark:border-emerald-800/50 dark:bg-emerald-900/20">
                    <p className="text-[9px] font-bold uppercase tracking-wider text-emerald-500 dark:text-emerald-400">Working</p>
                    <p className="mt-0.5 text-sm font-bold text-emerald-700 dark:text-emerald-300">{formatAttendanceHours(totals?.working_minutes)}</p>
                  </div>
                  <div className="rounded-xl border border-amber-100 bg-amber-50 px-3 py-2.5 text-center dark:border-amber-800/50 dark:bg-amber-900/20">
                    <p className="text-[9px] font-bold uppercase tracking-wider text-amber-500 dark:text-amber-400">Break</p>
                    <p className="mt-0.5 text-sm font-bold text-amber-700 dark:text-amber-300">{formatAttendanceHours(totals?.break_minutes)}</p>
                  </div>
                  <div className="rounded-xl border border-primary/20 bg-primary/5 px-3 py-2.5 text-center">
                    <p className="text-[9px] font-bold uppercase tracking-wider text-primary">Paid Hours</p>
                    <p className="mt-0.5 text-sm font-bold text-primary">{formatAttendanceHours(totals?.effective_working_minutes)}</p>
                    {(totals?.grace_minutes ?? 0) > 0 && (
                      <span className="mt-0.5 inline-flex w-fit items-center rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-bold text-primary">
                        +1h grace
                      </span>
                    )}
                  </div>
                  <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2.5 text-center dark:border-emerald-800/50 dark:bg-emerald-900/20">
                    <p className="text-[9px] font-bold uppercase tracking-wider text-emerald-500 dark:text-emerald-400">Check-Ins</p>
                    <p className="mt-0.5 text-sm font-bold text-emerald-700 dark:text-emerald-300">{totals?.check_ins ?? 0}</p>
                  </div>
                  <div className="rounded-xl border border-rose-100 bg-rose-50 px-3 py-2.5 text-center dark:border-rose-800/50 dark:bg-rose-900/20">
                    <p className="text-[9px] font-bold uppercase tracking-wider text-rose-500 dark:text-rose-400">Check-Outs</p>
                    <p className="mt-0.5 text-sm font-bold text-rose-700 dark:text-rose-300">{totals?.check_outs ?? 0}</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {!readOnly && editTarget && (
        <EditPunchModal
          log={editTarget}
          onClose={() => setEditTarget(null)}
          onSuccess={() => { void refetch(); setEditTarget(null); }}
        />
      )}

      {!readOnly && deleteTarget && (
        <DeletePunchModal
          log={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onSuccess={() => { setDeleteTarget(null); void refetch(); }}
        />
      )}

      {!readOnly && showAddPunch && (
        <AddPunchModal
          userId={userId}
          userName={userName}
          date={date}
          onClose={() => setShowAddPunch(false)}
          onSuccess={() => { void refetch(); setShowAddPunch(false); }}
        />
      )}
    </>,
    document.body,
  );
}
